import React from "react";

import { css } from "@emotion/css";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Resolver, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import type { ExistingPatient } from "@/models/ExistingPatientModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { ExistingPatientCard } from "@/uiKit/molecules/PatientCreationAndRenewal/ExistingPatientCard.tsx";
import {
  patientInfoSchema,
  type PatientInfo,
} from "@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientInfo.ts";
import {
  PATIENT_INFO_FORM_ID,
  PatientInfoFormContent,
} from "@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientInfoFormContent.tsx";

export type PatientInfoFormProps = {
  patientInfo?: Partial<PatientInfo>;
  onSubmit: (patientInfo: PatientInfo) => void;
  onBack?: (patientInfo: Partial<PatientInfo>) => void;
  className?: string;
};

export const PatientInfoForm: React.FC<PatientInfoFormProps> = ({ patientInfo, onSubmit, onBack, className }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const navigate = useNavigate();
  const [existingPatient, setExistingPatient] = React.useState<ExistingPatient>();
  const findExistingPatient = Queries.practitioner.useFindExistingPatient();

  const form = useForm<PatientInfo>({
    resolver: zodResolver(patientInfoSchema) as Resolver<PatientInfo>,
    defaultValues: patientInfo,
  });

  const email = useWatch({ name: "email", control: form.control });
  const phone = useWatch({ name: "phone", control: form.control });

  // The check is tied to the identity the doctor typed: editing it invalidates the previous verdict.
  React.useEffect(() => {
    setExistingPatient(undefined);
  }, [email, phone]);

  const submitWhenPatientIsNew = (data: PatientInfo) => {
    findExistingPatient.mutate(
      { email: data.email, phone: data.phone },
      {
        onSuccess: result => {
          if (result.status === "not_found") {
            onSubmit(data);
          } else {
            setExistingPatient(result);
          }
        },
        // A failing probe must not block a legitimate inclusion — the api still rejects duplicates on creation.
        onError: () => onSubmit(data),
      },
    );
  };

  return (
    <>
      <PatientInfoFormContent formContext={form} onSubmit={submitWhenPatientIsNew} className={className} />
      {existingPatient && existingPatient.status !== "not_found" && (
        <ExistingPatientCard
          status={existingPatient.status}
          onGoToPatient={
            existingPatient.patientId ? () => navigate({ to: `/patients/${existingPatient.patientId}` }) : undefined
          }
          className={styles.existingPatient}
        />
      )}
      <CardFooter>
        <Button
          variant="outlined"
          data-action="patient-creation.patient-info.cancel"
          id="patient-info-cancel"
          onClick={() => onBack && onBack(form.getValues())}
        >
          {t("button.cancel")}
        </Button>
        <Button
          form={PATIENT_INFO_FORM_ID}
          id="patient-info-next"
          type="submit"
          variant="contained"
          pending={findExistingPatient.isPending}
          data-action="patient-creation.patient-info.next"
        >
          {t("button.next")}
        </Button>
      </CardFooter>
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  existingPatient: css({
    margin: theme.spacing(0, 12),
  }),
});
